<template>
  <div class="app-header fw">
    <div id="app-header-left-icon" class="header-left">
      <!-- <div v-if="isPortal" class="fav__wrapper" title="收藏夹">
        <div class="fav__toggle" @click="handleFavToggle">
          <el-icon :size="20">
            <Star color="white" />
          </el-icon>
        </div>
      </div> -->
      <img v-if="appLogo" :src="appLogo" @click="handleLogoClick" />
      <div v-if="appTitle" class="title" @click="handleLogoClick">{{ appTitle }}</div>
    </div>
    <div class="header-body">
      <div class="header-menu">
        <el-tabs
          ref="headerMenuRef"
          v-model="activeTabName"
          class="header-menu-tabs"
          :scrollable="false"
          @tab-click="handleTabClick"
        >
          <el-tab-pane v-for="(it, index) in visibleTabItems" :key="it.name" :name="it.name">
            <template #label>
              <div :ref="(el: any) => setTabRefMap(index, el)" class="menu-content">
                <i v-if="it.icon" :class="`el-${it.icon}`" />
                {{ it.label }}
              </div>
            </template>
          </el-tab-pane>
          <el-tab-pane disabled>
            <template #label>
              <div ref="moreRef" class="header-menu-more fh" :class="{ invisible: !isShowMore }">
                <el-dropdown class="link-item fh" size="medium" trigger="click" @command="handleDropdownCommand">
                  <div class="dropdown-link fh">
                    <el-icon class="dropdown-link__icon">
                      <DArrowRight color="white" size="24px" />
                    </el-icon>
                  </div>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item
                        v-for="it in moreTabItems"
                        :Key="it.name"
                        class="dropdown-link__item"
                        :command="it"
                      >
                        <i v-if="it.icon" :class="`el-${it.icon}`" />
                        {{ it.label }}
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </template>
          </el-tab-pane>
        </el-tabs>
      </div>
      <div class="header-extra">
        <component v-if="headerExtraComponent" :is="headerExtraComponent" />
        <app-header-extra v-else />
      </div>
    </div>

    <c-downloads-dialog ref="downloadsDialogRef" />
  </div>
</template>

<script setup lang="ts">
import { _, useHostAppInstance, computed, ref, debound, onMounted, nextTick, onUnmounted, watch } from '@zto/zpage'
import { DArrowRight, Star } from '@element-plus/icons'

import { UI_GLOBAL_EVENTS } from '../../consts'

import CDownloadsDialog from '../file/CDownloadsDialog.vue'
import AppHeaderExtra from './AppHeaderExtra.vue'

const app = useHostAppInstance()

const emitter = app.emitter
const { appStore } = app.stores
const router = app.router
const appConfig = app.useAppConfig('', {})

const imageAssets = app.useAssets('images', {})

const isPortal = app.isPortal

const appLogo = appConfig.logo !== false ? appConfig.logo || imageAssets?.logo : null
const appTitle = appConfig.title
const headerExtraComponent = appConfig.header?.extra?.component

const displaySubmodules = appConfig.menu?.displayedSubmodules

const downloadsDialogRef = ref<any>()

const headerMenuRef = ref<any>()
const headerMenuTabRefArr: any[] = []

const moreRef = ref<any>()

const visiableTabIndex = ref<number>(-1)
const visiableTabWidth = ref<number>(0)

const headerMenuWidth = ref<number>(0)
const headerMenuTabsWidthArr = ref<number[]>([])

// 当前激活按钮
const activeTabName = ref(appStore.navMenu.submodule)

const setTabRefMap = (index: number, el: any) => {
  headerMenuTabRefArr[index] = el
}

const tabItems = computed(() => {
  let submodules = appStore.submodules
  if (displaySubmodules?.length) {
    submodules = submodules.filter(it => displaySubmodules.includes(it.title))
  }

  return submodules.map((it: any) => {
    return {
      name: it.name,
      icon: it.icon,
      label: it.title,
      path: it.path,
      submodule: it.submodule
    }
  })
})

const visibleTabItems = computed(() => {
  if (visiableTabIndex.value < 0) return tabItems.value

  return tabItems.value.slice(0, visiableTabIndex.value)
})

// 不可见tabItems
const moreTabItems = computed(() => {
  if (visiableTabIndex.value < 0) return []

  return tabItems.value.slice(visiableTabIndex.value)
})

// 是否显示更多
const isShowMore = computed(() => {
  return moreTabItems.value.length > 0
})

watch(router.currentRoute, val => {
  const name = val?.name ?? ''

  const parentName = getTopLevelParent(appStore.submodules || [], name)

  if (!parentName) {
    activeTabName.value = ''
  }

  if (parentName === activeTabName.value) return

  activeTabName.value = parentName
})

const resetVisiableTabIndexDebounce = debound(resetVisiableTabIndex, 500)

emitter.on(UI_GLOBAL_EVENTS.OPEN_DOWNLOADS, () => {
  downloadsDialogRef.value.show()
})

onMounted(() => {
  reloadMenusWidth()

  window.addEventListener('resize', handleWindowResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleWindowResize)
})

function handleFavToggle() {
  emitter.emit(UI_GLOBAL_EVENTS.TOGGLE_HOME_MENU)
}

function getTopLevelParent(nodes, targetName) {
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].name === targetName) {
      return nodes[i].name
    }
    if (nodes[i].children && nodes[i].children.length > 0) {
      const result = getTopLevelParent(nodes[i].children, targetName)
      if (result) {
        return nodes[i].name
      }
    }
  }
  return null
}

// 单击logo，返回首页
function handleLogoClick() {
  activeTabName.value = ''

  router.goHome()
}

function handleDropdownCommand(it: any) {
  gotoModule(it.name)

  activeTabName.value = it.name
}

// 菜单选中
function handleTabClick(a, b) {
  nextTick(() => {
    gotoModule(activeTabName.value)
  })
}

function handleWindowResize() {
  nextTick(() => {
    resetVisiableTabIndexDebounce()
  })
}

function gotoModule(name: string) {
  const tabItem = tabItems.value.find(it => it.name === name)

  // 使用setTimeout防止菜单卡顿
  setTimeout(() => {
    if (tabItem?.path) {
      appStore.changeSubmodule({ name: tabItem.submodule || '' })

      router.goto({ ...tabItem })
    } else {
      appStore.changeSubmodule({ name })
    }
  }, 5)
}

function reloadMenusWidth() {
  visiableTabIndex.value = -1

  const menuEl = headerMenuRef.value.$el

  if (!menuEl) return
  nextTick(() => {
    const tabsWidthArr: any[] = []

    for (let [index, it] of headerMenuTabRefArr.entries()) {
      const _pEl = it?.parentElement

      if (!_pEl) return

      tabsWidthArr[index] = _pEl.getBoundingClientRect().width
    }

    headerMenuTabsWidthArr.value = tabsWidthArr

    resetVisiableTabIndex()
  })
}

// 计算可显示
function resetVisiableTabIndex() {
  const menuEl = headerMenuRef.value.$el

  if (!menuEl || !headerMenuTabsWidthArr.value.length) {
    visiableTabIndex.value = -1
    return
  }

  const menuElWidth = menuEl.getBoundingClientRect().width - 80

  let visiableWidth = 0
  let visiableIndex = -1

  for (let [index, width] of headerMenuTabsWidthArr.value.entries()) {
    if (visiableWidth + width >= menuElWidth) {
      break
    }

    visiableWidth += width
    visiableIndex = index
  }

  visiableTabIndex.value = visiableIndex + 1
}
</script>

<style lang="scss" scoped>
$app-header-padding: 5px;
$app-header-inner-height: calc(var(--app-header-height) - $app-header-padding * 2);

.app-header {
  height: 100%;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  color: hsla(0, 0%, 100%, 0.8);
  background: linear-gradient(315deg, #5430ff, #3693ff);
}

.header-left {
  display: flex;
  align-items: center;
  padding: $app-header-padding;
  padding-left: 20px;
  padding-right: 50px;
  width: 110px;
  cursor: pointer;

  .fav {
    &__wrapper {
      padding-right: 10px;
      white-space: nowrap;
    }

    &__toggle {
      border-radius: 5px;
      padding: 2px;
      &:hover {
        background: rgba(11, 17, 29, 50);
      }
    }
  }

  & > img {
    height: $app-header-inner-height;
    float: left;
  }

  & > .title {
    display: inline-block;
    font-size: 18px;
    font-weight: 500;
    padding: 0 10px;
    line-height: $app-header-inner-height;
  }
}

.header-body {
  flex: 1;
  display: flex;
}

.header-menu-more {
  z-index: 100;

  &.invisible {
    visibility: hidden;
  }

  .dropdown-link {
    display: flex;
    align-items: center;
    cursor: pointer;

    .inner-text {
      color: white;
      white-space: nowrap;
    }
  }
}

.header-menu {
  position: relative;
  flex: 1;
  min-width: 100px;

  .menu-content {
    &.invisible {
      visibility: hidden;
    }
  }

  :deep(.el-tabs) {
    height: var(--app-header-height);

    .el-tabs {
      &__header {
        margin: 0;
        height: 100%;
      }

      &__nav {
        height: var(--app-header-height);
      }

      &__active-bar {
        background-color: white;
      }

      &__nav-wrap::after {
        background-color: transparent;
      }

      &__nav-next {
        visibility: hidden;
      }

      &__nav-prev {
        visibility: hidden;
      }

      &__item {
        height: var(--app-header-height);
        line-height: var(--app-header-height);
        opacity: 0.5;
        color: white;

        &.is-active {
          opacity: 1;
        }

        &:hover {
          opacity: 1;
        }
      }
    }
  }
}

.header-extra {
  padding: 0 20px;
}
</style>
